import React from 'react';
import {styled} from '@linaria/react';
import {mediaDesktopAndUp} from '../../../../constants/customMedia';
import {Badge} from './LinkBadges';
import {LinkItem} from './LinkItem';

export interface GridItem {
  badges: Badge[];
  label: string;
  textColor?: string;
  url: {url: string};
  saleIcon?: {color: {hex: string}};
}

interface LinksGridProps {
  gridItems: GridItem[];
  onLinkItemClick: () => void;
  maxRowsPerLinkSectionInTabletView: number;
  isSaleIndicationActive?: boolean;
}

export const LinksGrid: React.FC<LinksGridProps> = ({
  gridItems,
  onLinkItemClick,
  maxRowsPerLinkSectionInTabletView,
  isSaleIndicationActive,
}) => {
  if (!gridItems.length) {
    return <div />;
  }
  const gridItemsPerRow = getGridRowsForDesktop(gridItems.length);
  return (
    <LinksGridWrapper
      $itemsCount={gridItems.length}
      maxRowsPerLinkSectionInTabletView={maxRowsPerLinkSectionInTabletView}
      maxRowsPerLinkSectionInDesktop={gridItemsPerRow}
    >
      {gridItems.map(({badges, label, textColor, url, saleIcon}, index) => (
        <LinkItem
          key={`linkItem-${index}`}
          dataTestId={`linkItem-${index}`}
          label={label}
          onLinkItemClick={onLinkItemClick}
          url={url}
          textColor={textColor}
          badges={badges}
          saleIcon={isSaleIndicationActive ? saleIcon : undefined}
        />
      ))}
    </LinksGridWrapper>
  );
};

const getGridRowsForDesktop = (itemsAmount: number) => {
  // We want to evenly distribute link-items between columns.
  // Before with fixed amount of rows we had occurrences of having
  // only one item in the last column which looked weird. This solution
  // provides a more even distribution.
  if (itemsAmount > 8 && itemsAmount <= 20) {
    return Math.ceil(itemsAmount / 2);
  }

  if (itemsAmount > 20) {
    return Math.ceil(itemsAmount / 3);
  }

  return MAXIMUM_DESKTOP_GRID_ROWS;
};
const MAXIMUM_DESKTOP_GRID_ROWS = 8;
const LinksGridWrapper = styled.div<{
  $itemsCount: number;
  maxRowsPerLinkSectionInTabletView: number;
  maxRowsPerLinkSectionInDesktop: number;
}>`
  display: grid;
  gap: 14px 10px;
  grid-auto-flow: column;

  grid-template-rows: repeat(
    min(
      ${({$itemsCount}) => $itemsCount},
      ${({maxRowsPerLinkSectionInTabletView}) =>
        maxRowsPerLinkSectionInTabletView}
    ),
    min-content
  );

  @media ${mediaDesktopAndUp} {
    margin-bottom: 24px;
    grid-template-rows: repeat(
      min(
        ${({$itemsCount}) => $itemsCount},
        ${({maxRowsPerLinkSectionInDesktop}) => maxRowsPerLinkSectionInDesktop}
      ),
      min-content
    );
  }
`;
